/**
 * Moteur de la phase de devinette : tableau composite avec la retouche, pan/zoom
 * libre, un seul clic. Comme DrawingEngine, il vit hors de React.
 */
import {
  GUESS_BLIND_MS, type DrawZone, type Drawing } from '@retusch/shared';
import { ArtworkRenderer } from './canvas/ArtworkRenderer.js';
import { Compositor } from './canvas/Compositor.js';
import { StrokeCapture } from './canvas/StrokeCapture.js';
import { ZoneCanvas } from './canvas/ZoneCanvas.js';
import {
  type Rect,
  type Size,
  type Vec2,
  distanceToRect,
  ease,
  isInsideZone,
  zoneToRect,
} from './canvas/geometry.js';
import { MAX_POINT_DISTANCE, REVEAL_MS, ZONE_CANVAS_WIDTH } from './canvas/renderConfig.js';
import type { DevOptionsStore } from './devOptions.js';
import { naturalSize } from './artwork.js';

const GOLD = '#e8b84b';

export interface GuessResult {
  readonly found: boolean;
  readonly elapsedMs: number;
  /** Point clique, normalise 0-1 sur l'image. */
  readonly point: Vec2;
  /** Distance au rect de la zone, en pixels de l'image. 0 si trouve. */
  readonly distancePx: number;
}

export interface GuessEngineOptions {
  readonly canvas: HTMLCanvasElement;
  readonly image: HTMLImageElement;
  readonly zone: DrawZone;
  readonly drawing: Drawing;
  readonly palette: readonly string[];
  readonly dev: DevOptionsStore;
}

export class GuessEngine {
  private readonly options: GuessEngineOptions;
  private readonly onResult: (result: GuessResult) => void;

  private readonly renderer: ArtworkRenderer;
  private readonly compositor: Compositor;
  private readonly zoneCanvas: ZoneCanvas;
  private readonly capture: StrokeCapture;
  private readonly imageSize: Size;
  private readonly zoneRect: Rect;

  private startedAt = 0;
  private locked = false;
  private guessPoint: Vec2 | null = null;
  private hit = false;
  private revealStartedAt = 0;
  private unsubscribeDev: (() => void) | null = null;

  constructor(options: GuessEngineOptions, onResult: (result: GuessResult) => void) {
    this.options = options;
    this.onResult = onResult;

    this.imageSize = naturalSize(options.image);
    this.zoneRect = zoneToRect(options.zone, this.imageSize);

    this.renderer = new ArtworkRenderer(options.canvas, this.imageSize);
    this.compositor = new Compositor(
      options.image,
      this.imageSize,
      options.zone,
      ZONE_CANVAS_WIDTH,
    );

    this.zoneCanvas = new ZoneCanvas(this.zoneRect.height / this.zoneRect.width, options.palette);
    this.zoneCanvas.replay(options.drawing);
    this.compositor.invalidateZone();

    this.capture = new StrokeCapture(
      {
        element: options.canvas,
        mode: 'inspect',
        toView: (x, y) => this.renderer.clientToView(x, y),
        toZone: (x, y) => this.renderer.clientToNormalized(x, y),
        minPointDistance: () => MAX_POINT_DISTANCE,
        canDraw: () => false,
      },
      {
        onGesture: (delta) => {
          if (delta.dx !== 0 || delta.dy !== 0) this.renderer.panBy(delta.dx, delta.dy);
          if (delta.scale !== 1) this.renderer.zoomAt(delta.scale, delta.center);
        },
        onTap: (view) => this.handleTap(view),
      },
    );
  }

  start(): void {
    this.capture.attach();
    this.renderer.fit();
    this.renderer.start(this.render);
    this.startedAt = performance.now();

    this.unsubscribeDev = this.options.dev.subscribe(() => {
      this.compositor.invalidateZone();
      this.renderer.markDirty();
    });
  }

  destroy(): void {
    this.unsubscribeDev?.();
    this.unsubscribeDev = null;
    this.capture.detach();
    this.renderer.stop();
  }

  resize(): void {
    this.renderer.resize();
  }

  /** Temps ecoule depuis l'affichage, en ms. */
  elapsedMs(): number {
    return this.startedAt === 0 ? 0 : performance.now() - this.startedAt;
  }

  /** Timeout du timer : verrouille sans clic, donc rate. */
  timeout(): void {
    if (this.locked) return;
    this.locked = true;
    this.hit = false;
    this.guessPoint = null;
    this.revealStartedAt = performance.now();
    this.renderer.markDirty();
    this.onResult({
      found: false,
      elapsedMs: this.elapsedMs(),
      point: { x: 0, y: 0 },
      distancePx: Number.POSITIVE_INFINITY,
    });
  }

  private handleTap(view: Vec2): void {
    if (this.locked) return;
    // On sort de la phase de dessin : le clic qui devait prolonger un trait ne
    // doit pas devenir l'unique essai de la manche. Bloque AVANT tout effet
    // local, sinon le plateau se verrouillerait pour une devinette que le
    // serveur n'a jamais recue.
    if (this.elapsedMs() < GUESS_BLIND_MS) return;

    const rect = this.options.canvas.getBoundingClientRect();
    const normalized = this.renderer.clientToNormalized(view.x + rect.left, view.y + rect.top);

    // Verrouillage immediat : un seul essai, pas de retour en arriere.
    this.locked = true;
    this.guessPoint = normalized;
    this.hit = isInsideZone(normalized, this.options.zone);
    this.revealStartedAt = performance.now();
    this.renderer.markDirty();

    const imagePoint = {
      x: normalized.x * this.imageSize.width,
      y: normalized.y * this.imageSize.height,
    };

    this.onResult({
      found: this.hit,
      elapsedMs: this.elapsedMs(),
      point: normalized,
      distancePx: distanceToRect(imagePoint, this.zoneRect),
    });
  }

  private readonly render = (
    ctx: CanvasRenderingContext2D,
    _viewport: unknown,
    now: number,
  ): void => {
    const dev = this.options.dev.value;
    this.compositor.draw(ctx, this.renderer.getViewport(), this.zoneCanvas.canvas, {
      grain: dev.grain,
      feather: dev.feather,
    });

    const rect = this.viewZoneRect();

    if (dev.showZoneRect && !this.locked) {
      ctx.save();
      ctx.strokeStyle = '#ff00ff';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
      ctx.restore();
    }

    if (this.locked) {
      const t = ease(Math.min((now - this.revealStartedAt) / REVEAL_MS, 1));
      this.drawReveal(ctx, rect, t);
      if (t < 1) this.renderer.markDirty();
    }
  };

  private viewZoneRect(): Rect {
    const zone = this.options.zone;
    const topLeft = this.renderer.normalizedToView({ x: zone.x, y: zone.y });
    const bottomRight = this.renderer.normalizedToView({
      x: zone.x + zone.width,
      y: zone.y + zone.height,
    });
    return {
      x: topLeft.x,
      y: topLeft.y,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
    };
  }

  /** Lisere or qui se resserre sur la zone, plus un marqueur du clic si rate. */
  private drawReveal(ctx: CanvasRenderingContext2D, rect: Rect, t: number): void {
    const spread = (1 - t) * Math.max(rect.width, rect.height) * 0.6;

    ctx.save();
    ctx.strokeStyle = GOLD;
    ctx.globalAlpha = t;
    ctx.lineWidth = 2;
    ctx.shadowColor = GOLD;
    ctx.shadowBlur = 12 + (1 - t) * 24;
    ctx.strokeRect(
      rect.x - spread,
      rect.y - spread,
      rect.width + spread * 2,
      rect.height + spread * 2,
    );
    ctx.restore();

    const point = this.guessPoint;
    if (point === null || this.hit) return;

    const view = this.renderer.normalizedToView(point);
    ctx.save();
    ctx.globalAlpha = t;
    ctx.strokeStyle = '#f87171';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(view.x, view.y, 10, 0, Math.PI * 2);
    ctx.moveTo(view.x - 6, view.y - 6);
    ctx.lineTo(view.x + 6, view.y + 6);
    ctx.moveTo(view.x + 6, view.y - 6);
    ctx.lineTo(view.x - 6, view.y + 6);
    ctx.stroke();
    ctx.restore();
  }
}
